import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../config/api';
import { PAGE_PADDING } from '../styles/layout';
import { GOLD, GOLD_RGBA, BG, CARD_BG, INPUT_BG } from '../constants/booking';
import { formatStoredBookingDate } from '../utils/bookingDates';

const TEXT_PRIMARY = '#F5F0E8';
const TEXT_MUTED = '#9ca3af';

const pageStyle = {
  backgroundColor: BG,
  padding: PAGE_PADDING,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
};

const cardStyle = {
  backgroundColor: CARD_BG,
  borderRadius: '1rem',
  padding: '2.5rem',
  maxWidth: '600px',
  width: '100%',
  textAlign: 'center',
  border: `1px solid ${GOLD_RGBA}, 0.25)`,
};

const panelStyle = {
  backgroundColor: INPUT_BG,
  borderRadius: '0.75rem',
  padding: '1.5rem',
  marginBottom: '2rem',
  textAlign: 'left',
  border: `1px solid ${GOLD_RGBA}, 0.15)`,
};

const confirmBoxStyle = {
  backgroundColor: `${GOLD_RGBA}, 0.1)`,
  border: `1px solid ${GOLD_RGBA}, 0.4)`,
  borderRadius: '0.75rem',
  padding: '1rem',
  marginBottom: '2rem',
  textAlign: 'center',
};

const primaryBtnStyle = {
  backgroundColor: GOLD,
  color: '#000000',
  border: 'none',
  borderRadius: '0.75rem',
  padding: '1rem 2rem',
  fontSize: '1rem',
  fontWeight: '700',
  cursor: 'pointer',
};

const outlineBtnStyle = {
  backgroundColor: 'transparent',
  color: GOLD,
  border: `1px solid ${GOLD_RGBA}, 0.5)`,
  borderRadius: '0.75rem',
  padding: '1rem 2rem',
  fontSize: '1rem',
  fontWeight: '600',
  cursor: 'pointer',
};

const BookingSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const paymentIntentId = location.state?.paymentIntentId;
  // eslint-disable-next-line no-unused-vars
  const bookingData = location.state?.bookingData; // Kept for potential future use
  const customerInfo = location.state?.customerInfo;

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [pollingAttempts, setPollingAttempts] = useState(0); // Used for debugging/logging
  const [error, setError] = useState(null);

  // Poll for booking by payment intent ID with exponential backoff
  const pollForBooking = useCallback(async (isRetry = false) => {
    if (!paymentIntentId) {
      setError('Payment intent ID is required');
      setLoading(false);
      return;
    }

    if (!isRetry) {
      setLoading(true);
      setError(null);
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30; // Increased from 20 to 30 attempts (up to ~30 seconds total)
    const baseDelay = 500; // Start with 500ms
    const maxDelay = 2000; // Cap at 2 seconds

    const poll = async () => {
      while (attempts < maxAttempts && !cancelled) {
        try {
          // Get API base URL with fallback to production
          let apiBaseUrl = getApiBaseUrl();

          // If we got localhost but we're on production domain, use production URL
          if (apiBaseUrl && apiBaseUrl.includes('localhost') &&
              (window.location.hostname === 'digitaldivotgolf.com' ||
               window.location.hostname === 'www.digitaldivotgolf.com')) {
            console.warn('[BookingSuccess] Detected localhost API URL on production domain, switching to production API');
            apiBaseUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID || 'digital-divot'}.cloudfunctions.net/api`;
          }

          // Final fallback
          if (!apiBaseUrl || apiBaseUrl.includes('localhost')) {
            apiBaseUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID || 'digital-divot'}.cloudfunctions.net/api`;
          }

          const url = `${apiBaseUrl}/bookings/by-payment-intent/${paymentIntentId}`;

          console.log(`[BookingSuccess] Polling attempt ${attempts + 1}/${maxAttempts} for payment intent: ${paymentIntentId}`);
          console.log(`[BookingSuccess] Using API URL: ${apiBaseUrl}`);

          const response = await fetch(url);

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.booking) {
              console.log(`[BookingSuccess] ✅ Booking found!`, data.booking);
              setBooking(data.booking);
              setLoading(false);
              setError(null);
              return;
            } else {
              console.log(`[BookingSuccess] Response OK but no booking found yet`);
            }
          } else if (response.status === 404) {
            // Check if it's an endpoint 404 (API not found) vs booking 404 (booking not created yet)
            try {
              const errorData = await response.json();
              if (errorData.error && errorData.error.includes('Booking not found')) {
                // Booking not created yet - this is expected, keep polling
                console.log(`[BookingSuccess] Booking not found yet (404) - attempt ${attempts + 1}`);
              } else {
                // Endpoint doesn't exist - API function not deployed
                console.error(`[BookingSuccess] ❌ API endpoint not found! The /bookings/by-payment-intent endpoint may not be deployed.`);
                if (attempts >= 5) {
                  // After 5 attempts, stop polling if endpoint doesn't exist
                  throw new Error('API endpoint not available. Please contact support or try again later.');
                }
              }
            } catch (parseError) {
              // If we can't parse the error, assume it's an endpoint 404
              console.error(`[BookingSuccess] ❌ API endpoint may not exist (404) - attempt ${attempts + 1}`);
              if (attempts >= 5) {
                throw new Error('Unable to reach booking confirmation service. Your payment was successful - please check your email for confirmation.');
              }
            }
          } else {
            // Other errors are real errors
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error(`[BookingSuccess] Error fetching booking: ${response.status} ${response.statusText}`, errorText);
            // Stop polling after several errors
            if (attempts >= 10 && response.status >= 500) {
              throw new Error('Server error while confirming booking. Your payment was successful - please check your email.');
            }
          }
        } catch (err) {
          console.error(`[BookingSuccess] Polling error (attempt ${attempts + 1}):`, err);

          // If it's a user-facing error message, stop polling and show it
          if (err.message && (err.message.includes('endpoint') || err.message.includes('contact support') || err.message.includes('payment was successful'))) {
            setError(err.message);
            setLoading(false);
            return;
          }

          // For network errors, continue polling but stop after many attempts
          if (attempts >= 15) {
            setError('Unable to confirm booking status. Your payment was successful - please check your email for confirmation. If you don\'t receive an email, please contact support.');
            setLoading(false);
            return;
          }
        }

        attempts++;
        setPollingAttempts(attempts);

        // Exponential backoff: delay increases with each attempt, capped at maxDelay
        const delay = Math.min(baseDelay * Math.pow(1.2, attempts - 1), maxDelay);
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      if (!cancelled) {
        console.warn(`[BookingSuccess] ⚠️ Polling timed out after ${maxAttempts} attempts`);
        setError('Booking is being processed. Please check your email for confirmation. If you don\'t receive an email within a few minutes, please contact support.');
        setLoading(false);
      }
    };

    await poll();
  }, [paymentIntentId]);

  useEffect(() => {
    let cancelled = false;

    if (!cancelled) {
      pollForBooking();
    }

    return () => {
      cancelled = true;
    };
  }, [pollForBooking]);

  // Redirect if no payment intent ID
  if (!paymentIntentId) {
    navigate('/booking');
    return null;
  }

  // Show loading state while polling
  if (loading) {
    return (
      <div className="dd-page dd-booking-success" style={pageStyle}>
        <div style={cardStyle}>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: 'rgba(16, 185, 129, 0.15)',
            border: '2px solid #10b981',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 2rem',
            fontSize: '2rem',
          }}>
            ⏳
          </div>

          <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem', color: GOLD }}>
            Processing Your Booking...
          </h2>

          <p style={{ fontSize: '1.0625rem', color: TEXT_MUTED, lineHeight: '1.6' }}>
            Please wait while we confirm your booking. This usually takes just a few seconds.
          </p>
        </div>
      </div>
    );
  }

  // Show error state if polling timed out
  if (error && !booking) {
    return (
      <div className="dd-page dd-booking-success" style={pageStyle}>
        <div style={cardStyle}>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: `${GOLD_RGBA}, 0.12)`,
            border: `2px solid ${GOLD_RGBA}, 0.45)`,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 2rem',
            fontSize: '2rem',
          }}>
            ⏳
          </div>

          <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem', color: GOLD }}>
            Processing...
          </h2>

          <p style={{ fontSize: '1.0625rem', color: TEXT_MUTED, marginBottom: '2rem', lineHeight: '1.6' }}>
            {error}
          </p>

          <div style={{ ...confirmBoxStyle, textAlign: 'left' }}>
            <p style={{ fontSize: '0.875rem', color: GOLD, margin: '0 0 0.5rem', fontWeight: '600' }}>
              What's happening?
            </p>
            <p style={{ fontSize: '0.875rem', color: TEXT_MUTED, margin: 0, lineHeight: '1.5' }}>
              Your payment was successful! We're processing your booking confirmation. This usually takes just a few seconds, but can sometimes take up to a minute. Check your email ({customerInfo?.email || 'the email you provided'}) for a confirmation message.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                setError(null);
                pollForBooking(true);
              }}
              style={{ ...primaryBtnStyle, backgroundColor: '#10b981', color: TEXT_PRIMARY }}
            >
              Check Again
            </button>
            <button onClick={() => navigate('/booking')} style={primaryBtnStyle}>
              Return to Booking
            </button>
          </div>
        </div>
      </div>
    );
  }

  const packageLabel = booking?.packageName || booking?.package?.name || '—';

  // Show success state when booking is found
  return (
    <div className="dd-page dd-booking-success" style={pageStyle}>
      <div style={cardStyle}>
        <div style={{
          width: '80px',
          height: '80px',
          backgroundColor: 'rgba(16, 185, 129, 0.15)',
          border: '2px solid #10b981',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 2rem',
          fontSize: '2rem',
        }}>
          ✅
        </div>

        <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem', color: GOLD }}>
          Booking Confirmed!
        </h2>

        <p style={{ fontSize: '1.0625rem', color: TEXT_MUTED, marginBottom: '1.5rem', lineHeight: '1.6' }}>
          Thank you for your booking! We&apos;ve sent a confirmation email with your confirmation code.
        </p>

        <div style={confirmBoxStyle}>
          <p style={{ fontSize: '0.875rem', fontWeight: '600', color: GOLD, margin: '0 0 0.5rem' }}>
            Confirmation Code
          </p>
          <p style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: TEXT_PRIMARY,
            margin: 0,
            fontFamily: 'monospace',
            letterSpacing: '0.1em',
          }}>
            {booking?.confirmationCode || booking?.bookingId}
          </p>
          <p style={{ fontSize: '0.8125rem', color: TEXT_MUTED, margin: '0.75rem 0 0' }}>
            Save this code and use it with your booking email on Find Booking to look up your reservation.
          </p>
        </div>

        <div style={panelStyle}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '1rem', color: GOLD }}>
            Booking Details
          </h3>

          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {[
              ['Package', packageLabel],
              ['Date', booking?.date ? formatStoredBookingDate(booking.date) : '—'],
              ['Time', booking?.time || '—'],
              ['Total', booking?.total != null ? `$${booking.total}` : '—'],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <span style={{ color: TEXT_MUTED }}>{label}:</span>
                <span style={{ fontWeight: '600', color: TEXT_PRIMARY, textAlign: 'right' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/find-booking')} style={primaryBtnStyle}>
            Find Booking
          </button>
          <button onClick={() => navigate('/booking')} style={outlineBtnStyle}>
            Book Another Session
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccess;
